import type {Theme} from '@emotion/react';

interface VariablesProps {
    theme: Theme;
}

/**
 * Generates CSS custom properties for default dropdown input colors.
 *
 * @param props       The properties object.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS custom properties string for default dropdown input styling.
 */
export const getBaseVariables = ({theme}: VariablesProps) => `
    --label-color: ${theme.colors.textPrimary};
    --chosen-option-border-color: ${theme.colors.neutral20};
    --chosen-option-background-color: ${theme.colors.neutral20};
    --chosen-option-font-color: ${theme.colors.textPrimary};
    --chosen-option-placeholder-font-color: ${theme.colors.textSecondary};
    --delete-icon-color: ${theme.colors.textPrimary};
    --chevron-color: ${theme.colors.primary};
    --cursor: pointer;
`;

/**
 * Generates error state CSS variables for the dropdown input component.
 *
 * @param props       The properties object.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS variables string for error dropdown styling.
 */
export const getErrorVariables = ({theme}: VariablesProps) => `
    --chosen-option-border-color: ${theme.colors.error};
    --label-color: ${theme.colors.textPrimary};
`;

/**
 * Generates disabled state CSS variables for the dropdown input component.
 *
 * @param props       The properties object.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS variables string for disabled dropdown styling.
 */
export const getDisabledVariables = ({theme}: VariablesProps) => `
    --label-color: ${theme.colors.neutral60};
    --chosen-option-font-color: ${theme.colors.neutral40};
    --chosen-option-placeholder-font-color: ${theme.colors.neutral40};
    --chosen-option-border-color: ${theme.colors.neutral20};
    --chosen-option-background-color: ${theme.colors.neutral20};
    --delete-icon-color: ${theme.colors.neutral40};
    --chevron-color: ${theme.colors.neutral40};
    --cursor: not-allowed;
`;

/**
 * Generates focus state CSS variables for the dropdown input component.
 *
 * @param props       The properties object.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS variables string for focused dropdown styling.
 */
export const getFocusVariables = ({theme}: VariablesProps) => `
    --label-color: ${theme.colors.primary};
    --chosen-option-border-color: ${theme.colors.primary};
    --chosen-option-background-color: ${theme.colors.neutral0};
    --cursor: pointer;
`;